import * as React from "react"
import { useState, useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { Grid, FormControlLabel, Checkbox, Box, Typography, TextField, InputAdornment, Autocomplete, Popover, Tooltip, IconButton } from "@mui/material"
import { AccordionSeeker, IconSeeker } from "@ui-seeker/core"
import { getSubmit } from "@ui-seeker/utils"

import { SitesMultiSelect } from "@components"
import { BACKEND_APIS } from "@constants"
import {
    selectedCustomer, selectedSites, selectedBrands, searchText,
    onlyUnmatched, onlySuspicious, compareSite, isLoadingFilters, canSearch,
    recentDataEnabled, recentDataDays, recentDataDateFrom, recentDataDateTo,
    onlyNewProducts, newProductsDateFrom, newProductsDateTo
} from "../@signals"

function formatDateLabel(from, to) {
    if (!from && !to) return 'Only new (7d)'
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
    if (from && to) return `New: ${formatDate(from)} - ${formatDate(to)}`
    if (from) return `New: from ${formatDate(from)}`
    if (to) return `New: until ${formatDate(to)}`
    return 'Only new (7d)'
}


function formatRecentDataLabel(days, from, to) {
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
    if (from || to) {
        if (from && to) return `Recent: ${formatDate(from)} - ${formatDate(to)}`
        if (from) return `Recent: from ${formatDate(from)}`
        if (to) return `Recent: until ${formatDate(to)}`
    }
    return `Recent data (${days}d)`
}


export default function RetailerProductsFilters() {
    const isOpen = useSignal(true)
    const [customers, setCustomers] = useState([])
    const [sites, setSites] = useState([])
    const [brands, setBrands] = useState([])
    const [compareSites, setCompareSites] = useState([])
    const [recentDataAnchor, setRecentDataAnchor] = useState(null)
    const [dateAnchor, setDateAnchor] = useState(null)

    const hasCustomRecentDates = recentDataDateFrom.value || recentDataDateTo.value
    const hasCustomRecentDays = recentDataDays.value !== 30
    const hasCustomDates = newProductsDateFrom.value || newProductsDateTo.value

    // Load customers on mount
    useEffect(() => {
        getSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/customers`)
            .then(res => { if (res) setCustomers(res) })
            .catch(() => console.error('Error loading customers'))
    }, [])

    // Load sites, brands and compare sites when customer changes
    useEffect(() => {
        const customerId = selectedCustomer.value?.value
        if (!customerId) {
            setSites([])
            setBrands([])
            setCompareSites([])
            return
        }

        getSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/sites?id_customer=${customerId}`)
            .then(res => { if (res) setSites(res) })
            .catch(() => console.error('Error loading sites'))

        getSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/brands?id_customer=${customerId}`)
            .then(res => { if (res) setBrands(res) })
            .catch(() => console.error('Error loading brands'))

        getSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/all-sites?id_customer=${customerId}`)
            .then(res => { if (res) setCompareSites(res) })
            .catch(() => console.error('Error loading compare sites'))
    }, [selectedCustomer.value?.value])

    const handleSearch = () => {
        if (canSearch.value) {
            isLoadingFilters.value = true
        }
    }

    const handleClear = () => {
        selectedCustomer.value = null
        selectedSites.value = []
        selectedBrands.value = []
        searchText.value = ''
        onlyUnmatched.value = false
        onlySuspicious.value = false
        compareSite.value = null
        onlyNewProducts.value = false
        newProductsDateFrom.value = null
        newProductsDateTo.value = null
        recentDataEnabled.value = true
        recentDataDays.value = 30
        recentDataDateFrom.value = null
        recentDataDateTo.value = null
    }

    const handleClearRecentData = () => {
        recentDataDays.value = 30
        recentDataDateFrom.value = null
        recentDataDateTo.value = null
    }

    return <AccordionSeeker
        title="Filters"
        TransitionProps={{ unmountOnExit: false }}
        expanded={isOpen.value}
        onChange={() => isOpen.value = !isOpen.value}
        footerActions={
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%', p: 1 }}>
                <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer', color: '#9ca3af', '&:hover': { color: '#ef4444' }, alignSelf: 'center', mr: 1 }}
                    onClick={handleClear}
                >
                    Clear
                </Typography>
                <Box
                    component="button"
                    onClick={handleSearch}
                    disabled={!canSearch.value}
                    sx={{
                        px: 3, py: 1,
                        bgcolor: canSearch.value ? '#3b82f6' : '#e5e7eb',
                        color: canSearch.value ? 'white' : '#9ca3af',
                        border: 'none', borderRadius: 1,
                        cursor: canSearch.value ? 'pointer' : 'default',
                        fontWeight: 600, fontSize: 14,
                        '&:hover': canSearch.value ? { bgcolor: '#2563eb' } : {}
                    }}
                >
                    Search
                </Box>
            </Box>
        }
    >
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
                <Autocomplete
                    options={customers}
                    getOptionLabel={(opt) => opt.label || ''}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    value={selectedCustomer.value}
                    onChange={(_, val) => {
                        selectedCustomer.value = val
                        selectedSites.value = []
                        selectedBrands.value = []
                    }}
                    renderInput={(params) => <TextField {...params} label="Customer" size="small" />}
                    size="small"
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <SitesMultiSelect
                    options={sites}
                    value={selectedSites.value}
                    onChange={(newValue) => selectedSites.value = newValue}
                    label="Site"
                    disabled={!selectedCustomer.value}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    multiple
                    options={brands}
                    getOptionLabel={(opt) => opt.label || ''}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    value={selectedBrands.value}
                    onChange={(_, val) => selectedBrands.value = val || []}
                    disabled={!selectedCustomer.value}
                    renderInput={(params) => <TextField {...params} label="Brand" size="small" />}
                    size="small"
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    options={compareSites}
                    getOptionLabel={(opt) => opt.label || ''}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    value={compareSite.value}
                    onChange={(_, val) => compareSite.value = val}
                    disabled={!selectedCustomer.value}
                    renderInput={(params) => <TextField {...params} label="Compare with site" size="small" />}
                    size="small"
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, ID, EAN, ref"
                    value={searchText.value}
                    onChange={(e) => searchText.value = e.target.value}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSeeker icon="search" sx={{ fontSize: 16, color: '#9ca3af' }} />
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {/* Recent Data Filter */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={recentDataEnabled.value}
                                onChange={(e) => recentDataEnabled.value = e.target.checked}
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconSeeker icon="clock-rotate-left" sx={{ fontSize: 14, color: '#10b981' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatRecentDataLabel(recentDataDays.value, recentDataDateFrom.value, recentDataDateTo.value)}
                                </Typography>
                            </Box>
                        }
                        sx={{ m: 0 }}
                    />
                    <Tooltip title="Configure recent data range">
                        <IconButton
                            size="small"
                            onClick={(e) => setRecentDataAnchor(e.currentTarget)}
                            sx={{
                                ml: 0.5,
                                color: (hasCustomRecentDates || hasCustomRecentDays) ? '#10b981' : '#9ca3af',
                                '&:hover': { color: '#10b981' }
                            }}
                        >
                            <IconSeeker icon="sliders" sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(recentDataAnchor)}
                        anchorEl={recentDataAnchor}
                        onClose={() => setRecentDataAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ color: '#10b981', mb: 0.5 }}>
                                Recent data filter (by last seen)
                            </Typography>
                            <TextField
                                label="Days"
                                type="number"
                                size="small"
                                fullWidth
                                value={recentDataDays.value}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10)
                                    if (val > 0) recentDataDays.value = val
                                }}
                                InputProps={{ inputProps: { min: 1 } }}
                                disabled={hasCustomRecentDates}
                                helperText={hasCustomRecentDates ? "Using custom date range" : "Products seen in last N days"}
                            />
                            <Typography variant="caption" sx={{ color: '#9ca3af', textAlign: 'center' }}>
                                — or use custom dates —
                            </Typography>
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                fullWidth
                                value={recentDataDateFrom.value || ''}
                                onChange={(e) => recentDataDateFrom.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                fullWidth
                                value={recentDataDateTo.value || ''}
                                onChange={(e) => recentDataDateTo.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            {(hasCustomRecentDates || hasCustomRecentDays) && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#10b981', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={handleClearRecentData}
                                    >
                                        Reset to default (30d)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Popover>
                </Box>

                {/* Only New Products Filter */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={onlyNewProducts.value}
                                onChange={(e) => onlyNewProducts.value = e.target.checked}
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconSeeker icon="sparkles" sx={{ fontSize: 14, color: '#6366f1' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatDateLabel(newProductsDateFrom.value, newProductsDateTo.value)}
                                </Typography>
                            </Box>
                        }
                        sx={{ m: 0 }}
                    />
                    <Tooltip title="Custom date range">
                        <IconButton
                            size="small"
                            onClick={(e) => setDateAnchor(e.currentTarget)}
                            sx={{
                                ml: 0.5,
                                color: hasCustomDates ? '#6366f1' : '#9ca3af',
                                '&:hover': { color: '#6366f1' }
                            }}
                        >
                            <IconSeeker icon="calendar-days" sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(dateAnchor)}
                        anchorEl={dateAnchor}
                        onClose={() => setDateAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ color: '#6366f1', mb: 0.5 }}>
                                Custom date range (first added)
                            </Typography>
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                fullWidth
                                value={newProductsDateFrom.value || ''}
                                onChange={(e) => newProductsDateFrom.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                fullWidth
                                value={newProductsDateTo.value || ''}
                                onChange={(e) => newProductsDateTo.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            {hasCustomDates && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#6366f1', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={() => {
                                            newProductsDateFrom.value = null
                                            newProductsDateTo.value = null
                                        }}
                                    >
                                        Reset to default (7d)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Popover>
                </Box>

                {/* Only Unmatched */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={onlyUnmatched.value}
                            onChange={(e) => onlyUnmatched.value = e.target.checked}
                            size="small"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconSeeker icon="link-slash" sx={{ fontSize: 14, color: '#ef4444' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Only unmatched
                            </Typography>
                        </Box>
                    }
                    sx={{ m: 0 }}
                />

                {/* Suspicious Matches */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={onlySuspicious.value}
                            onChange={(e) => onlySuspicious.value = e.target.checked}
                            size="small"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconSeeker icon="triangle-exclamation" sx={{ fontSize: 14, color: '#f59e0b' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Suspicious matches
                            </Typography>
                        </Box>
                    }
                    sx={{ m: 0 }}
                />
            </Grid>
        </Grid>
    </AccordionSeeker>
}
